import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule, Pin } from 'lucide-angular';
import {
  Thermometer,
  Flame,
  Waves,
  Wind,
  Snowflake,
  Fish,
  TreePine,
  Droplets,
  AlertTriangle,
  TrendingDown,
  FileText,
  Users,
  ShieldAlert,
  Briefcase,
  Wheat,
  Bug,
  Fuel,
} from 'lucide-angular';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { CompanyObjectivesDataService } from 'src/app/shared/services/company-objectives-data.service';
import { NotificationService } from 'src/app/core/services/notification.service';

export interface ClimateRisk {
  id: number;
  icon: typeof Thermometer;
  titleKey: string;
  descriptionKey: string;
}

export const CLIMATE_RISKS: ClimateRisk[] = [
  {
    id: 1,
    icon: Fuel,
    titleKey: 'objectives.risks.kerosene.title',
    descriptionKey: 'objectives.risks.kerosene.description',
  },
  {
    id: 2,
    icon: Thermometer,
    titleKey: 'objectives.risks.heat.title',
    descriptionKey: 'objectives.risks.heat.description',
  },
  {
    id: 3,
    icon: Flame,
    titleKey: 'objectives.risks.wildfire.title',
    descriptionKey: 'objectives.risks.wildfire.description',
  },
  {
    id: 4,
    icon: Waves,
    titleKey: 'objectives.risks.coastal.title',
    descriptionKey: 'objectives.risks.coastal.description',
  },
  {
    id: 5,
    icon: Wind,
    titleKey: 'objectives.risks.storms.title',
    descriptionKey: 'objectives.risks.storms.description',
  },
  {
    id: 6,
    icon: Snowflake,
    titleKey: 'objectives.risks.snow.title',
    descriptionKey: 'objectives.risks.snow.description',
  },
  {
    id: 7,
    icon: Fish,
    titleKey: 'objectives.risks.coral.title',
    descriptionKey: 'objectives.risks.coral.description',
  },
  {
    id: 8,
    icon: TreePine,
    titleKey: 'objectives.risks.desertification.title',
    descriptionKey: 'objectives.risks.desertification.description',
  },
  {
    id: 9,
    icon: Droplets,
    titleKey: 'objectives.risks.water.title',
    descriptionKey: 'objectives.risks.water.description',
  },
  {
    id: 10,
    icon: AlertTriangle,
    titleKey: 'objectives.risks.transport.title',
    descriptionKey: 'objectives.risks.transport.description',
  },
  {
    id: 11,
    icon: TrendingDown,
    titleKey: 'objectives.risks.carbonPricing.title',
    descriptionKey: 'objectives.risks.carbonPricing.description',
  },
  {
    id: 12,
    icon: FileText,
    titleKey: 'objectives.risks.regulations.title',
    descriptionKey: 'objectives.risks.regulations.description',
  },
  {
    id: 13,
    icon: Users,
    titleKey: 'objectives.risks.capacity.title',
    descriptionKey: 'objectives.risks.capacity.description',
  },
  {
    id: 14,
    icon: ShieldAlert,
    titleKey: 'objectives.risks.greenwashing.title',
    descriptionKey: 'objectives.risks.greenwashing.description',
  },
  {
    id: 15,
    icon: Briefcase,
    titleKey: 'objectives.risks.esg.title',
    descriptionKey: 'objectives.risks.esg.description',
  },
  {
    id: 16,
    icon: Wheat,
    titleKey: 'objectives.risks.food.title',
    descriptionKey: 'objectives.risks.food.description',
  },
  {
    id: 17,
    icon: Bug,
    titleKey: 'objectives.risks.disease.title',
    descriptionKey: 'objectives.risks.disease.description',
  },
];

@Component({
  selector: 'app-climate-risks',
  standalone: true,
  imports: [TranslocoModule, LucideAngularModule],
  templateUrl: './climate-risks.component.html',
})
export class ClimateRisksComponent implements OnInit {
  private currentUserService = inject(CurrentUserService);
  private objectivesDataService = inject(CompanyObjectivesDataService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  readonly risks = CLIMATE_RISKS;
  readonly Pin = Pin;

  isSaving = signal(false);

  selectedRisks = this.objectivesDataService.selectedVulnerabilities;

  sortedRisks = computed(() => {
    const selected = this.selectedRisks();
    return [...this.risks].sort((a, b) => {
      const aRank = selected.includes(a.id) ? 0 : 1;
      const bRank = selected.includes(b.id) ? 0 : 1;
      return aRank - bRank;
    });
  });

  ngOnInit(): void {
    this.currentUserService.loadCurrentUser().subscribe();
  }

  isSelected(riskId: number): boolean {
    return this.selectedRisks().includes(riskId);
  }

  toggleRisk(riskId: number): void {
    if (this.isSaving()) return;

    const current = this.selectedRisks();
    const updated = current.includes(riskId)
      ? current.filter((id) => id !== riskId)
      : [...current, riskId];

    this.isSaving.set(true);
    this.objectivesDataService.updateSelectedVulnerabilities(updated).subscribe({
      next: () => this.isSaving.set(false),
      error: () => {
        this.isSaving.set(false);
        this.notificationService.error(
          this.translocoService.translate('objectives.risksSaveError')
        );
      },
    });
  }
}
